import { useState, useContext, useEffect, useCallback } from 'react';
import { Box, Button, Stack } from '@mui/material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { BannerComponent } from 'components/shared/BannerComponent';
import { CardContext } from 'context/cards/cardContext';
import { HeaderComponent } from 'components/shared/HeaderComponent';
import { OPEN_CREATE_NEW_CARD, UPDATE_CARDS_METADATA } from 'context/cards/types';
import { OwnershipDisclosureModal } from './OwnershipDisclosureModal';
import AccountsTable from './Accounts/Accounts';
import AlertDialog from 'components/shared/AlertDialog/AlertDialog';
import BlueToolTip from 'components/shared/BlueTooltip';
import CreateCard from 'components/CardsSection/CreateCard/CreateCard';
import FundingHistoryTable from './FundingHistoryTable';
import NoBalanceDialog from 'components/CardsSection/dialogs/NoBalanceDialog';
import OverviewStatistics from './OverviewStatistics/OverviewStatistics';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import useCardSettings from 'Hooks/useCardSettings';
// Services
import { Overview } from 'lib/api';
// Utils and Helpers
import { hasAvailableFunds, hasCreditLine, hasCreditModel, hasDepositModel, hasPINModel } from 'helpers/businesses';
import { isAdminUser } from 'helpers/auth';
import { NO_FUNDS_TEXT, NO_FUNDS_TITLE, KYB_STATUS } from 'helpers/constants';

const { REACT_APP_AR_FRONTEND_URL } = process.env;

const OverviewSection = () => {
    const { dispatch, statistics } = useContext(CardContext);
    const { hasAccount, businessInfo, userInfo } = useContext(AuthContext);
    const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
    const [snackbarLabel, setSnackbarLabel] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [ownershipDisclosureModalOpen, setOwnershipDisclosureModalOpen] = useState(false);
    const dashboardLoginURL = `${REACT_APP_AR_FRONTEND_URL}/v2/ar/customers/${businessInfo.business_ar_id}/overview`;
    const cardSettings = useCardSettings();

    const fetchOverviewInfo = useCallback(async () => {
        try {
            const response = await Overview.getOverviewInfo();
            dispatch({
                type: UPDATE_CARDS_METADATA,
                payload: response
            });
        } catch (error) {
            console.error(error);
        }
    }, [dispatch]);
    useEffect(() => {
        if (userInfo.kyb === 'approved') {
            fetchOverviewInfo();
        }
    }, [fetchOverviewInfo, userInfo]);

    useEffect(() => {
        if (
            !businessInfo.is_ownership_disclosure_signed &&
            businessInfo.status === KYB_STATUS.APPROVED &&
            userInfo.is_business_creator
        ) {
            setOwnershipDisclosureModalOpen(true);
        }
    }, [businessInfo, userInfo]);

    const handleCreateCardClick = () => {
        if (!hasAvailableFunds(businessInfo, statistics)) {
            return setIsAlertDialogOpen(true);
        }
        if (hasAccount) {
            return dispatch({ type: OPEN_CREATE_NEW_CARD });
        }

        setSnackbarLabel('KYC / KYB approval required');
        setIsSnackbarOpen(true);
    };

    const onAlertButtonClickEvent = () => {
        if (hasPINModel(businessInfo)) {
            window.open(`${REACT_APP_AR_FRONTEND_URL}/v2/ar/customers/${businessInfo.business_ar_id}/overview`, '_blank');
        }
        setIsAlertDialogOpen(false);
    };

    const isCreateCardDisabled =
        businessInfo?.is_frozen ||
        businessInfo?.is_disabled ||
        (hasPINModel(businessInfo) && !Number(statistics.accountBalance)) ||
        !cardSettings.isCreationEnabled;

    return (
        <>
            <Stack style={{ width: '100%', overflowX: 'hidden' }}>
                <Stack direction="row">
                    <Box sx={{ flexGrow: 1 }}>
                        <HeaderComponent title="Overview" />
                    </Box>
                    {hasPINModel(businessInfo) && (
                        <Button variant="outlined" style={{ marginRight: '15px' }} onClick={() => window.open(dashboardLoginURL)}>
                            Fund Account
                        </Button>
                    )}
                    {isAdminUser(userInfo?.role?.id) && isCreateCardDisabled && (
                        <BlueToolTip
                            placement="left"
                            arrow
                            title="The card creation service is temporarily unavailable due to system maintenance.">
                            <span>
                                <Button
                                    onClick={handleCreateCardClick}
                                    variant="contained"
                                    data-testid="overview-create-card-button"
                                    disabled={isCreateCardDisabled}>
                                    Create Card
                                </Button>
                            </span>
                        </BlueToolTip>
                    )}
                    {isAdminUser(userInfo?.role?.id) && !isCreateCardDisabled && (
                        <Button onClick={handleCreateCardClick} variant="contained" data-testid="overview-create-card-button">
                            Create Card
                        </Button>
                    )}
                </Stack>

                <Box marginTop="24px">
                    <OverviewStatistics />
                </Box>
                <Box marginTop="30px">
                    <BannerComponent />
                </Box>
                {!hasDepositModel(businessInfo) && (
                    <Box>
                        <AccountsTable />
                    </Box>
                )}
                <Box>
                    <FundingHistoryTable />
                </Box>
                <CreateCard />
                <NoBalanceDialog />
                {ownershipDisclosureModalOpen && (
                    <OwnershipDisclosureModal setOwnershipDisclosureModalOpen={setOwnershipDisclosureModalOpen} />
                )}
                <AlertDialog
                    isOpen={isAlertDialogOpen}
                    title={
                        hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                            ? NO_FUNDS_TITLE.CREDIT_MODEL
                            : NO_FUNDS_TITLE.PREPAID_MODEL
                    }
                    description={
                        hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                            ? NO_FUNDS_TEXT.CREDIT_MODEL
                            : NO_FUNDS_TEXT.PREPAID_MODEL
                    }
                    onButtonClick={onAlertButtonClickEvent}
                    buttonText={hasPINModel(businessInfo) ? 'Add Funds' : 'OK'}
                />
                <StatusSnackbar label={snackbarLabel} severity={'error'} open={isSnackbarOpen} setOpenAlert={setIsSnackbarOpen} />
            </Stack>
        </>
    );
};

export default OverviewSection;
